import { infiniteQueryOptions, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import {
  DOCUMENTS_PAGE_QUERY_KEY,
  DOCUMENTS_QUERY_KEY,
  documentsQueryFn,
  type StoredDocument,
  TOOLS_QUERY_KEY,
  toolsQueryFn,
} from "@/core/lib/document-store";
import {
  type DocumentsPageParams,
  fetchDocumentsPage,
  fetchDocumentTypes,
} from "@/core/lib/documents-api";
import type { CustomTool } from "@/core/schema/tool-schema";

/**
 * Live readers for the document store.
 *
 * Both documents and custom tools are now server-backed, so {@link useDocuments}
 * and {@link useTools} read through React Query — one fetch shared across every
 * list, cached between navigations, and re-rendered the instant an imperative
 * `upsert/delete` writes the same cache.
 */

/** Stable, newest-first ordering applied by React Query's `select` (memoized). */
function sortDocuments(docs: StoredDocument[]): StoredDocument[] {
  return [...docs].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

function useDocumentsQuery() {
  return useQuery({
    queryKey: DOCUMENTS_QUERY_KEY,
    queryFn: documentsQueryFn,
    select: sortDocuments,
  });
}

export function useDocuments(): StoredDocument[] {
  return useDocumentsQuery().data ?? [];
}

/**
 * Like {@link useDocuments}, but also surfaces the initial-fetch state so lists
 * can show a skeleton instead of flashing their empty state before the
 * server-backed cache has loaded. `isLoading` is only true on the very first
 * load (no cached data yet), not on background refetches.
 */
export function useDocumentsState(): {
  documents: StoredDocument[];
  isLoading: boolean;
} {
  const { data, isLoading } = useDocumentsQuery();
  return { documents: data ?? [], isLoading };
}

/** The filters that define a paginated library view (page size + query). */
export type DocumentsPageFilters = Omit<DocumentsPageParams, "limit" | "offset"> & {
  pageSize: number;
};

/**
 * Query options for one filtered view of the library, shared by the hook below
 * and the route loader — which warms this exact key as the navigation starts,
 * so the request is already in flight by the time the page mounts.
 */
export function documentsPageOptions(filters: DocumentsPageFilters) {
  const { pageSize, q, type, status, sort, dir } = filters;
  return infiniteQueryOptions({
    queryKey: [...DOCUMENTS_PAGE_QUERY_KEY, { pageSize, q, type, status, sort, dir }],
    queryFn: ({ pageParam }) =>
      fetchDocumentsPage({
        limit: pageSize,
        offset: pageParam,
        q,
        type,
        status,
        sort,
        dir,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((n, p) => n + p.items.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
  });
}

/**
 * Server-paginated documents library with "load more". Each filter change
 * starts a fresh query; pages append as the user loads more. Returns the
 * flattened rows plus the server `total` so the view can show counts and decide
 * whether more remain.
 */
export function useDocumentsPage(filters: DocumentsPageFilters) {
  const query = useInfiniteQuery(documentsPageOptions(filters));

  const documents = useMemo(() => query.data?.pages.flatMap((p) => p.items) ?? [], [query.data]);
  const total = query.data?.pages[0]?.total ?? 0;

  return {
    documents,
    total,
    isLoading: query.isLoading,
    isError: query.isError,
    fetchNextPage: query.fetchNextPage,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
  };
}

/** Distinct document type labels for the library's type filter. */
export function useDocumentTypes(): string[] {
  return (
    useQuery({
      queryKey: [...DOCUMENTS_PAGE_QUERY_KEY, "types"],
      queryFn: fetchDocumentTypes,
    }).data ?? []
  );
}

/** Stable, newest-updated-first ordering applied by React Query's `select`. */
function sortTools(tools: CustomTool[]): CustomTool[] {
  return [...tools].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

export function useTools(): CustomTool[] {
  return (
    useQuery({
      queryKey: TOOLS_QUERY_KEY,
      queryFn: toolsQueryFn,
      select: sortTools,
    }).data ?? []
  );
}

export function useToolsState(): {
  tools: CustomTool[];
  isLoading: boolean;
} {
  const { data, isLoading } = useQuery({
    queryKey: TOOLS_QUERY_KEY,
    queryFn: toolsQueryFn,
    select: sortTools,
  });

  return { tools: data ?? [], isLoading };
}
